<template>
  <div class="discounts-page">
    <loader :isShow="isLoading" :textInfo="''" />

    <div class="discounts-content">
      <header class="discounts-header">
        <div class="header-text">
          <h1 class="discounts-title">Discount Settings</h1>
          <p class="discounts-subtitle">
            Weekly &amp; monthly discount rates by room
          </p>
        </div>
        <button
          class="refresh-btn"
          :disabled="isLoading"
          @click="loadDiscounts"
          title="Refresh"
        >
          <svg
            class="refresh-icon"
            :class="{ spinning: isLoading }"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.5 10A7.5 7.5 0 1 1 10 2.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <path
              d="M10 2.5L13 5.5L10 8.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>Refresh</span>
        </button>
      </header>

      <section class="discounts-card">
        <!-- Error state -->
        <div v-if="errorMessage" class="state-wrapper">
          <div class="state-icon error-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="1.8"
              />
              <path
                d="M12 7v5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
          </div>
          <p class="state-title error-text">Failed to load discounts</p>
          <p class="state-desc">{{ errorMessage }}</p>
          <button class="retry-btn" @click="loadDiscounts">Try again</button>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!isLoading && discounts.length === 0"
          class="state-wrapper"
        >
          <div class="state-icon empty-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="3"
                stroke="currentColor"
                stroke-width="1.8"
              />
              <path
                d="M8 12h8M8 8h8M8 16h4"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <p class="state-title">No discounts found</p>
          <p class="state-desc">
            There are no discount settings configured yet.
          </p>
        </div>

        <!-- Loaded table -->
        <template v-else-if="!isLoading">
          <div class="card-toolbar">
            <div class="toolbar-left">
              <span class="room-count-pill">
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                >
                  <rect
                    x="1"
                    y="1"
                    width="14"
                    height="14"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M5 8h6M5 5.5h6M5 10.5h3.5"
                    stroke="currentColor"
                    stroke-width="1.3"
                    stroke-linecap="round"
                  />
                </svg>
                {{ discounts.length }}
                {{ discounts.length === 1 ? 'room' : 'rooms' }}
              </span>
            </div>
            <div class="toolbar-right">
              <span class="legend-item">
                <span class="legend-dot weekly-dot"></span>Weekly
              </span>
              <span class="legend-item">
                <span class="legend-dot monthly-dot"></span>Monthly
              </span>
            </div>
          </div>

          <div class="table-scroll">
            <table class="discounts-table">
              <thead>
                <tr>
                  <th class="col-room">Room</th>
                  <th class="col-discount">
                    <span class="th-label weekly-label">Weekly</span>
                  </th>
                  <th class="col-discount">
                    <span class="th-label monthly-label">Monthly</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in discounts"
                  :key="getRoomName(row)"
                  :class="{ 'row-alt': index % 2 !== 0 }"
                >
                  <td class="col-room">
                    <div class="room-cell">
                      <span class="room-index">{{ index + 1 }}</span>
                      <span class="room-name">{{ getRoomName(row) }}</span>
                    </div>
                  </td>
                  <td class="col-discount">
                    <span
                      class="discount-badge"
                      :class="getBadgeClass(row.weeklyDiscount)"
                    >
                      {{ row.weeklyDiscount }}%
                    </span>
                  </td>
                  <td class="col-discount">
                    <span
                      class="discount-badge"
                      :class="getBadgeClass(row.monthlyDiscount)"
                    >
                      {{ row.monthlyDiscount }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue'
import roomsApi from '@/api/rooms'

export default {
  name: 'DiscountSettingsScreen',
  components: {
    Loader
  },
  data() {
    return {
      isLoading: false,
      discounts: [],
      errorMessage: ''
    }
  },
  methods: {
    getRoomName(row) {
      return `${row.propertyName || ''} - ${row.internalName || ''}`
    },
    getBadgeClass(value) {
      const n = parseFloat(value) || 0
      if (n === 0) return 'badge-zero'
      if (n < 10) return 'badge-low'
      if (n < 20) return 'badge-mid'
      return 'badge-high'
    },
    async loadDiscounts() {
      this.errorMessage = ''
      this.isLoading = true

      const res = await roomsApi.getUplistingDiscounts()

      if (res && res.error) {
        this.errorMessage =
          typeof res.error === 'string'
            ? res.error
            : res.error.message || 'Failed to load discounts.'
        this.discounts = []
        this.isLoading = false
        return
      }

      const rows = (res && res.data) || []
      this.discounts = rows
        .filter(
          (row) => row.weeklyDiscount != null && row.monthlyDiscount != null
        )
        .slice()
        .sort((a, b) => this.getRoomName(a).localeCompare(this.getRoomName(b)))
      this.isLoading = false
    }
  },
  async mounted() {
    await this.loadDiscounts()
  }
}
</script>

<style scoped>
/* ── Page shell ─────────────────────────────────────────── */
.discounts-page {
  min-height: 100%;
  padding: 32px 24px 56px;
  background-color: #f5f6f8;
  box-sizing: border-box;
}

.discounts-content {
  max-width: 800px;
  margin: 0 auto;
}

/* ── Header ─────────────────────────────────────────────── */
.discounts-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
  gap: 16px;
  flex-wrap: wrap;
}

.header-text {
  flex: 1;
}

.discounts-title {
  margin: 0 0 5px;
  font-family: Montserrat-Bold, Montserrat, sans-serif;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.3px;
  color: #1a1a1a;
}

.discounts-subtitle {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: #767675;
}

/* ── Refresh button ─────────────────────────────────────── */
.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border: 1px solid #cbd4dc;
  border-radius: 6px;
  background: #ffffff;
  color: #4a4a4a;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.refresh-btn:hover:not(:disabled) {
  border-color: #66b015;
  color: #66b015;
  background: #f4fae8;
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.refresh-icon.spinning {
  animation: spin 0.8s linear infinite;
}

/* ── Card ───────────────────────────────────────────────── */
.discounts-card {
  background: #ffffff;
  border: 1px solid #e3e8ec;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

/* ── Toolbar ────────────────────────────────────────────── */
.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 20px;
  border-bottom: 1px solid #eef1f3;
  background: #fafbfc;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.room-count-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #eef5e6;
  color: #3d6b0f;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #767675;
  font-weight: 500;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.weekly-dot {
  background: #66b015;
}
.monthly-dot {
  background: #3d6b0f;
}

/* ── Table ──────────────────────────────────────────────── */
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.discounts-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.discounts-table thead th {
  padding: 12px 20px;
  font-size: 11px;
  font-family: Montserrat-Bold, Montserrat, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #9aa4b0;
  background: #ffffff;
  border-bottom: 1px solid #eef1f3;
}

.discounts-table tbody td {
  padding: 14px 20px;
  font-size: 13.5px;
  line-height: 1.4;
  vertical-align: middle;
}

.discounts-table tbody tr {
  transition: background-color 0.12s ease;
}

.discounts-table tbody tr.row-alt {
  background-color: #fafbfc;
}

.discounts-table tbody tr:hover {
  background-color: #f4fae8 !important;
}

.discounts-table tbody tr:not(:last-child) td {
  border-bottom: 1px solid #f0f2f4;
}

/* ── Column sizing ──────────────────────────────────────── */
.col-room {
  width: 55%;
  text-align: left;
}

.col-discount {
  width: 22.5%;
  text-align: center;
}

/* ── Room cell ──────────────────────────────────────────── */
.room-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.room-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f0f2f4;
  color: #767675;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.room-name {
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.35;
}

/* ── Table header labels ────────────────────────────────── */
.th-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* ── Discount badges ────────────────────────────────────── */
.discount-badge {
  display: inline-block;
  min-width: 54px;
  padding: 5px 13px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.1px;
}

.badge-zero {
  background: #f2f3f5;
  color: #9aa4b0;
}

.badge-low {
  background: #e8f5d5;
  color: #4e800e;
}

.badge-mid {
  background: #d4edaa;
  color: #3a6409;
}

.badge-high {
  background: #b8e068;
  color: #2a4d04;
}

/* ── Empty / Error states ───────────────────────────────── */
.state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 24px;
  text-align: center;
  gap: 6px;
}

.state-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.state-icon svg {
  width: 26px;
  height: 26px;
}

.error-icon {
  background: #fdf2f3;
  color: #df000f;
}

.empty-icon {
  background: #f0f5fb;
  color: #9aa4b0;
}

.state-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
}

.state-title.error-text {
  color: #df000f;
}

.state-desc {
  margin: 0;
  font-size: 13px;
  color: #767675;
  max-width: 320px;
  line-height: 1.5;
}

.retry-btn {
  margin-top: 14px;
  padding: 8px 20px;
  border: 1px solid #df000f;
  border-radius: 6px;
  background: transparent;
  color: #df000f;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.retry-btn:hover {
  background: #df000f;
  color: #ffffff;
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .discounts-page {
    padding: 20px 16px 40px;
  }

  .discounts-title {
    font-size: 22px;
  }

  .discounts-subtitle {
    font-size: 13px;
  }

  .discounts-table thead th,
  .discounts-table tbody td {
    padding: 11px 14px;
    font-size: 12.5px;
  }

  .col-room {
    width: 48%;
  }
  .col-discount {
    width: 26%;
  }

  .discount-badge {
    min-width: 44px;
    padding: 4px 10px;
    font-size: 12px;
  }

  .toolbar-right {
    display: none;
  }
}
</style>
